/**
 * ManageTab — the console's configuration drawer (D-7).
 *
 * Three of classic's seven top-level tabs were not daily work at all: batches
 * are checked when one is running, and Tuning and Review are admin surfaces
 * visited weekly at most. They cost every recruiter two thirds of the tab strip
 * every day. Here they are one tab with a small sub-nav, and the sub rides the
 * URL (`?tab=manage&sub=…`) so the weekly-review email's link still lands on the
 * exact panel it always did.
 *
 * Tuning and Review are shown only to CRM admins. The pages themselves keep
 * their 403 empty-states as defense — this is about not offering a recruiter a
 * door that opens onto a permission error. Batches loses nothing for anyone: it
 * is the panel everybody can use, and it is the default.
 *
 * The batches drill-through state (which row is open, its targets) stays on the
 * PAGE, not here, so flipping tabs keeps a batch expanded exactly as classic did.
 */
import { Loader2 } from 'lucide-react';
import { FilterBar, FilterChip } from '../../../components/filters';
import { useIsCRMAdmin } from '../../../lib';
import BatchesPanel, { type BatchRow, type BatchTargetRow } from '../BatchesPanel';
import AgentReview from '../AgentReview';
import AgentTuning from '../AgentTuning';
import type { ManageSub } from './tabParams';

export interface ManageTabProps {
  sub: ManageSub;
  onSubChange: (sub: ManageSub) => void;
  batches: BatchRow[];
  batchesAgentEnabled: boolean;
  busyId: string | null;
  openBatchId: string | null;
  batchDetail: { id: string; targets: BatchTargetRow[] } | null;
  loading: boolean;
  onToggleBatch: (id: string) => void;
  onCancelBatch: (id: string) => Promise<void>;
  onOpenConversation: (conversationId: string) => void;
}

export default function ManageTab({
  sub, onSubChange, batches, batchesAgentEnabled, busyId, openBatchId, batchDetail,
  loading, onToggleBatch, onCancelBatch, onOpenConversation,
}: ManageTabProps) {
  const isAdmin = useIsCRMAdmin();
  // A non-admin who arrives on ?sub=tuning (an old bookmark, a shared link) gets
  // the panel they can actually use rather than an empty 403 screen with no chip
  // selected. The URL is left alone — the console owns it.
  const active: ManageSub = isAdmin ? sub : 'batches';

  const subs: { value: ManageSub; label: string; title: string }[] = [
    { value: 'batches', label: 'Batches', title: 'Re-engagement batches and the conversations they opened' },
    ...(isAdmin ? ([
      { value: 'tuning' as const, label: 'Tuning', title: 'Chat-to-edit the prompts, briefs and settings the agent runs on' },
      { value: 'review' as const, label: 'Review', title: 'The Monday weekly review — what the agent did and what to change' },
    ]) : []),
  ];

  return (
    <div className="mt-3">
      <FilterBar>
        {subs.map((s) => (
          <FilterChip key={s.value} active={active === s.value} onClick={() => onSubChange(s.value)} title={s.title}>
            {s.label}
          </FilterChip>
        ))}
      </FilterBar>

      {active === 'tuning' ? (
        <AgentTuning />
      ) : active === 'review' ? (
        <AgentReview />
      ) : loading && batches.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />
        </div>
      ) : (
        <BatchesPanel
          batches={batches}
          agentEnabled={batchesAgentEnabled}
          busyId={busyId}
          openBatchId={openBatchId}
          batchDetail={batchDetail}
          onToggleBatch={onToggleBatch}
          onCancelBatch={onCancelBatch}
          onOpenConversation={onOpenConversation}
        />
      )}
    </div>
  );
}
